import {
  Cancel01Icon,
  Delete03Icon,
  InformationCircleIcon,
  StatusIcon,
  UserCircle02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  leadApi,
  type CreateLeadBulkActionPayload,
  type LeadBulkActionKind,
  type LeadsFilterPayload
} from "api/lead";
import { toast } from "react-toastify";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  InputSize,
  Modal,
  IconButtonVariant,
  Select,
  Show
} from "shared/components";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { Avatar } from "shared/components/Avatar";
import { useState } from "react";
import { getErrorMessage } from "utils/error";
import { useLeadBulkActionProgress } from "providers/LeadBulkActionProgress";
import {
  isSelectionEmpty,
  type LeadSelection
} from "shared/app/LeadsTableView/hooks/useLeadSelection";
import { AssigneeSelect } from "./AssigneeSelect";
import { StageOption } from "./types";

// Above this many leads a delete needs the word typed out, in either selection mode
// (decision 5 on CRM-1449). A page holds at most 50, so this only bites multi-page and
// filter-wide selections.
const TYPED_DELETE_CONFIRM_THRESHOLD = 100;
const DELETE_CONFIRMATION_WORD = "DELETE";

const pluralLeads = (count: number) => (count === 1 ? "lead" : "leads");

const getDeleteLeadsTitle = (count: number) => {
  if (count <= 1) return "Are you sure you want to delete this lead?";
  return `Are you sure you want to delete ${count.toLocaleString()} leads?`;
};

const FILTER_SCOPE_NOTE =
  "This applies to every lead matching your current filters, including those not shown on this page.";

const getExplicitIds = (selection: LeadSelection): number[] =>
  selection.mode === "ids" ? Array.from(selection.ids) : [];

const getAssignButtonLabel = ({
  isUnassign,
  isFilterMode,
  countLabel
}: {
  isUnassign: boolean;
  isFilterMode: boolean;
  countLabel: string;
}) => {
  if (isUnassign) return `Unassign ${countLabel}`;
  return isFilterMode ? `Assign ${countLabel}` : "Assign";
};

const getStageButtonLabel = (isFilterMode: boolean, countLabel: string) =>
  isFilterMode ? `Move ${countLabel}` : "Apply";

const getDeleteButtonLabel = (isFilterMode: boolean, countLabel: string) =>
  isFilterMode ? `Delete ${countLabel}` : "Delete";

const getDeleteDescription = (isFilterMode: boolean) => {
  const base =
    "This action cannot be undone. All associated data, notes, and activity will be permanently removed.";
  return isFilterMode ? `${base} ${FILTER_SCOPE_NOTE}` : base;
};

const getToolbarHint = (filterActionsBlocked: boolean) =>
  filterActionsBlocked
    ? "Another bulk action is still running"
    : "Across all pages matching your filters";

export const SelectPopUp = ({
  selection,
  count,
  filterPayload,
  stageOptions,
  onClearSelection
}: {
  selection: LeadSelection;
  /** Effective selected count (filter mode: total minus exclusions). */
  count: number;
  /** Flat /leads filter params the list is currently showing; sent as-is in filter mode. */
  filterPayload: LeadsFilterPayload;
  stageOptions: StageOption[];
  onClearSelection: () => void;
}) => {
  const queryClient = useQueryClient();
  const { startTracking, isRunning: isBulkActionRunning } =
    useLeadBulkActionProgress();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<number | null>(
    null
  );
  // The picker's "Unassigned" option also reports null, so null alone cannot tell
  // "nothing chosen yet" from "chose to unassign". Only an explicit pick may submit —
  // otherwise one click on a fresh modal would strip the assignee from every lead.
  const [hasPickedAssignee, setHasPickedAssignee] = useState(false);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const isFilterMode = selection.mode === "filter";

  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedAssigneeId(null);
    setHasPickedAssignee(false);
  };
  const closeStageModal = () => {
    setIsStageModalOpen(false);
    setSelectedStageId(null);
  };

  // ---- filter mode: one async run on the server (CRM-1449) ----------------------
  const { mutateAsync: startBulkAction, isPending: isStartingBulkAction } =
    useMutation({
      mutationKey: ["BULK", "FILTER_ACTION", "LEADS", "CREATE"],
      mutationFn: (payload: CreateLeadBulkActionPayload) =>
        leadApi.createBulkAction(payload),
      onSuccess: (run) => {
        startTracking(run);
        closeAssignModal();
        closeStageModal();
        setIsConfirmDeleteOpen(false);
        onClearSelection();
      },
      onError: (error) => {
        toast.error(getErrorMessage(error, "Failed to start the bulk action"));
      }
    });

  const runFilterAction = (
    action: LeadBulkActionKind,
    params: Pick<CreateLeadBulkActionPayload, "stage_id" | "assignee_ids">
  ) => {
    if (selection.mode !== "filter") return;
    void startBulkAction({
      action,
      ...params,
      excluded_lead_ids: Array.from(selection.excludedIds),
      filters: filterPayload
    });
  };

  // ---- ids mode: the three synchronous endpoints, unchanged ---------------------
  const { mutateAsync: deleteLeads, isPending: isDeleting } = useMutation({
    mutationKey: ["BULK", "DELETE", "LEADS"],
    onSuccess: (_, variables) => {
      setIsConfirmDeleteOpen(false);
      onClearSelection();
      queryClient.refetchQueries({ queryKey: ["LEADS_TABLE"] });
      toast.success(
        `${variables.length} ${pluralLeads(variables.length)} deleted successfully`
      );
    },
    onError: () => {
      toast.error("Failed to delete leads");
    },
    mutationFn: async (lead_ids: number[]) => {
      return instance.delete(`/portal/v1/leads/bulk_delete`, {
        data: {
          lead_ids
        }
      });
    }
  });

  const { mutateAsync: bulkAssignLeads, isPending: isAssigning } = useMutation({
    mutationKey: ["BULK", "ASSIGN", "LEADS"],
    onSuccess: () => {
      onClearSelection();
      closeAssignModal();
      queryClient.refetchQueries({ queryKey: ["LEADS_TABLE"] });
      queryClient.refetchQueries({ queryKey: ["LEAD_ACTIVITY_LOG"] });
      toast.success("Leads assigned successfully");
    },
    onError: () => {
      toast.error("Failed to assign leads");
    },
    mutationFn: async ({
      lead_ids,
      assignee_ids
    }: {
      lead_ids: number[];
      assignee_ids: number[];
    }) => {
      return leadApi.bulkAssignLeads({
        lead_ids,
        assignee_ids
      });
    }
  });

  const { mutateAsync: bulkChangeStage, isPending: isChangingStage } =
    useMutation({
      mutationKey: ["BULK", "CHANGE_STAGE", "LEADS"],
      mutationFn: leadApi.bulkChangeStage,
      onSuccess: () => {
        onClearSelection();
        closeStageModal();
        queryClient.refetchQueries({ queryKey: ["LEADS_TABLE"] });
        queryClient.refetchQueries({ queryKey: ["LEAD_ACTIVITY_LOG"] });
        toast.success("Stage updated");
      },
      onError: () => {
        toast.error("Failed to update stage");
      }
    });

  // ---- handlers -----------------------------------------------------------------
  const handleConfirmBulkDelete = () => {
    if (isFilterMode) {
      runFilterAction("delete", {});
      return;
    }
    deleteLeads(getExplicitIds(selection));
  };

  const handleCloseConfirmDelete = () => {
    if (isDeleting || isStartingBulkAction) return;
    setIsConfirmDeleteOpen(false);
  };

  const handleBulkAssign = () => {
    if (!hasPickedAssignee) return;
    const assignee_ids =
      selectedAssigneeId !== null ? [selectedAssigneeId] : [];
    if (isFilterMode) {
      runFilterAction("assign", { assignee_ids });
      return;
    }
    const leadIds = getExplicitIds(selection);
    if (leadIds.length === 0) return;
    bulkAssignLeads({ lead_ids: leadIds, assignee_ids });
  };

  const handleBulkChangeStage = () => {
    if (selectedStageId === null) return;
    if (isFilterMode) {
      runFilterAction("change_stage", { stage_id: selectedStageId });
      return;
    }
    const leadIds = getExplicitIds(selection);
    if (leadIds.length === 0) return;
    bulkChangeStage({ lead_ids: leadIds, stage_id: selectedStageId });
  };

  if (isSelectionEmpty(selection) || count === 0) return null;

  const countLabel = `${count.toLocaleString()} ${pluralLeads(count)}`;
  // The server refuses a second filter-wide run per account while one is in flight;
  // mirror that up front instead of surfacing the 422 after the confirm dialog.
  const filterActionsBlocked = isFilterMode && isBulkActionRunning;
  const isUnassign = hasPickedAssignee && selectedAssigneeId === null;
  const assignButtonLabel = getAssignButtonLabel({
    isUnassign,
    isFilterMode,
    countLabel
  });
  const isSubmittingFilterAction = isFilterMode && isStartingBulkAction;
  const needsTypedDeleteConfirmation = count > TYPED_DELETE_CONFIRM_THRESHOLD;

  const deleteDescription = getDeleteDescription(isFilterMode);

  return (
    <>
      <div className="h-16 divide-x-[1px] shadow-lg bg-white px-4 py-2 rounded-lg border border-alpha-100 z-[11] fixed right-10 bottom-24 animate-appear flex justify-end items-center gap-4">
        <div className="flex flex-col min-w-0">
          <p className="text-base font-medium text-content-1000 truncate">
            {countLabel} selected
          </p>
          <Show if={isFilterMode}>
            <p className="text-xs text-content-400 truncate">
              {getToolbarHint(filterActionsBlocked)}
            </p>
          </Show>
        </div>

        <Button
          size={ButtonSize.SM}
          HugeIcon={UserCircle02Icon}
          variant={ButtonVariant.TEXT}
          disabled={filterActionsBlocked}
          onClick={() => setIsAssignModalOpen(true)}
          className="flex items-center justify-center gap-2 pl-4"
        >
          Assign
        </Button>
        <Button
          size={ButtonSize.SM}
          HugeIcon={StatusIcon}
          variant={ButtonVariant.TEXT}
          disabled={filterActionsBlocked}
          onClick={() => setIsStageModalOpen(true)}
          className="flex items-center justify-center gap-2 pl-4"
        >
          Change stage
        </Button>
        <Button
          size={ButtonSize.SM}
          HugeIcon={Delete03Icon}
          loading={isDeleting}
          disabled={filterActionsBlocked}
          variant={ButtonVariant.TEXT}
          className="flex items-center justify-center gap-2 pl-4"
          onClick={() => setIsConfirmDeleteOpen(true)}
        >
          Delete
        </Button>

        <div className="pl-4 h-5 flex flex-shrink-0 items-center">
          <IconButton
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            HugeIcon={Cancel01Icon}
            onClick={onClearSelection}
          />
        </div>
      </div>

      <Modal
        opened={isAssignModalOpen}
        onClose={closeAssignModal}
        panelClassName="w-full max-w-[400px] p-6"
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-xl leading-6 font-bold text-content-1000">
            Assign {countLabel}
          </h3>
          <Show if={isFilterMode}>
            <p className="text-sm text-content-400">
              {FILTER_SCOPE_NOTE} The new assignee will be notified for each
              lead.
            </p>
          </Show>
          <div className="flex flex-col gap-2">
            <AssigneeSelect
              value={selectedAssigneeId}
              onChange={(assignee_id) => {
                setSelectedAssigneeId(assignee_id);
                setHasPickedAssignee(true);
              }}
              label="Assign to"
            />
            <Show if={isUnassign}>
              <p className="text-sm text-warning-700">
                This removes the current assignee from {countLabel}.
              </p>
            </Show>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.BASE}
              onClick={closeAssignModal}
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              loading={isAssigning || isSubmittingFilterAction}
              disabled={!hasPickedAssignee}
              onClick={handleBulkAssign}
            >
              {assignButtonLabel}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        opened={isStageModalOpen}
        onClose={closeStageModal}
        panelClassName="w-full max-w-[400px] p-6"
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-xl leading-6 font-bold text-content-1000">
            Change stage for {countLabel}
          </h3>
          <Show if={isFilterMode}>
            <p className="text-sm text-content-400">
              {FILTER_SCOPE_NOTE} Stage automations and notifications will run
              for each lead.
            </p>
          </Show>
          <Select
            id="bulk-change-stage-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            size={InputSize.SM}
            label="New stage"
            value={selectedStageId ?? undefined}
            options={stageOptions}
            onChange={(e) => setSelectedStageId(Number(e.target.value))}
            placeholder="Select a stage"
            formatOptionLabel={(option) => {
              const stageOption = option as StageOption;
              return (
                <div className="flex items-center gap-2 py-1">
                  <Avatar
                    fallbackText=""
                    fallbackBackgroundColor={stageOption.color}
                  />
                  <span className="text-base text-content-1000">
                    {stageOption.label}
                  </span>
                </div>
              );
            }}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.BASE}
              onClick={closeStageModal}
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              loading={isChangingStage || isSubmittingFilterAction}
              disabled={selectedStageId === null}
              onClick={handleBulkChangeStage}
            >
              {getStageButtonLabel(isFilterMode, countLabel)}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        opened={isConfirmDeleteOpen}
        onClose={handleCloseConfirmDelete}
        onConfirm={handleConfirmBulkDelete}
        title={getDeleteLeadsTitle(count)}
        description={deleteDescription}
        confirmationInput={
          needsTypedDeleteConfirmation
            ? {
                label: `Type ${DELETE_CONFIRMATION_WORD} to confirm`,
                expected: DELETE_CONFIRMATION_WORD
              }
            : undefined
        }
        variant="danger"
        icon={{ icon: InformationCircleIcon, variant: "danger" }}
        confirmButton={{
          label: getDeleteButtonLabel(isFilterMode, countLabel),
          loading: isDeleting || isSubmittingFilterAction
        }}
        cancelButton={{
          label: "Cancel",
          disabled: isDeleting || isSubmittingFilterAction
        }}
      />
    </>
  );
};
